import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { authStorage, mirrorSessionTokens } from '../utils/authStorage';

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      hasHydrated: false,

      setAuth: (user, accessToken, refreshToken) => {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        });
      },

      clearAuth: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },

      // Called by the axios interceptor after a silent token refresh, so the
      // Zustand store (and anything reading accessToken from it) never goes
      // stale relative to the raw localStorage copy used for outgoing requests.
      setAccessToken: (accessToken) => {
        localStorage.setItem('accessToken', accessToken);
        set({ accessToken });
      },

      // Same as setAccessToken, for the rotated refresh token a silent
      // refresh gets back (ROTATE_REFRESH_TOKENS blacklists the one that was
      // just used, so this is now the only refresh token that still works).
      setRefreshToken: (refreshToken) => {
        localStorage.setItem('refreshToken', refreshToken);
        set({ refreshToken });
      },

      updateUser: (userData) => {
        set({ user: { ...get().user, ...userData } });
      },

      hasRole: (role) => {
        const { user } = get();
        return user?.roles?.some((r) => r.name === role) || false;
      },

      isAdministrator: () => get().hasRole('administrator'),
      isPlatformOwner: () => Boolean(get().user?.is_platform_owner),
      isTeacher: () => get().hasRole('teacher'),
      isStudent: () => get().hasRole('student'),
      isParent: () => get().hasRole('parent'),

      // Alias used by components (e.g. the top nav's Logout button).
      logout: () => get().clearAuth(),
      setHasHydrated: (value) => set({ hasHydrated: value }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => authStorage),
      onRehydrateStorage: () => (state) => {
        mirrorSessionTokens(state);
        state?.setHasHydrated(true);
      },
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export default useAuthStore;
